import { openPlanModal } from '../../../openPlanModal';
import { ConfirmDialog } from 'components/Actions/createConfirmDialog';
import eq from 'fast-deep-equal';
import type { Supplier } from 'modules/supplier/supplier/types';
import type { GridRow } from 'modules/campaign/plan/types';
import type { RowPatch } from '../../../diff';
import { checkHasValue } from './checkHasValue';

export type SupplierPatchResult =
  // value unchanged — nothing to send
  | { kind: 'noop' }
  // user dismissed the "Keep values?" modal — caller may keep the editor open
  | { kind: 'dismissed' }
  // a patch to apply
  | { kind: 'patch'; patch: RowPatch };

/**
 * Shared supplier-change logic for EditSupplier and supplier clipboard paste:
 * skip no-ops, ask "Keep values?" when the target row carries keepable values
 * for the new supplier's direction, and set `_reset` accordingly so the backend
 * clears or preserves dependent fields. Awaits the confirm modal.
 */
export async function resolveSupplierPatch(row: GridRow, supplier: Supplier | null): Promise<SupplierPatchResult> {
  // product kind must match the row kind — the picker filters by kind, but the
  // clipboard-paste path can feed a mismatched supplier
  if (supplier && supplier.product.kind !== row.kind) return { kind: 'noop' };
  if (eq(row.supplier, supplier)) return { kind: 'noop' };

  let _keep_values = false;
  if (checkHasValue(row, supplier)) {
    const res = await openPlanModal((props) => <ConfirmDialog {...props} title="Keep values?" />, {
      testId: 'confirm-dialog',
      returnFocus: true,
    });
    // dismissed (cancel) — bail without mutating
    if (typeof res === 'undefined') return { kind: 'dismissed' };
    if (res) _keep_values = true;
  }

  return { kind: 'patch', patch: { id: row.id, supplier, _reset: !_keep_values } };
}
